import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import { useListAdminCredentialsQuery, usePreviewSourceMutation } from '../../api/admin.api';
import { describeQueryError } from '../../api/describeQueryError.api';
import { ReturnLink } from '../../components/ReturnLink';
import { ADMIN_NEW_CREDENTIAL_PATH, ADMIN_REPOSITORIES_PATH } from '../../config';
import { contentWidthSx, pageGutterSx, sectionSpacingSx } from '../../theme';
import { AccessOffer } from './AccessOffer';
import { SourceDraft } from './SourceDraft';

/**
 * `/admin/repositories/new` — paste a repository's address, Check it, fill in what the
 * repository does not say, and Pull it into the library.
 *
 * **Check writes nothing.** It asks the importer what pulling would do and shows the
 * answer. **With no key chosen the importer tries every way in it has**, public first,
 * then a GitHub App, then a token, and says which got in. **Only a key for the
 * repository's own host is tried.** When none does (a private repository a host will not
 * describe answers 404, which is indistinguishable from one that does not exist) the page
 * shows what was tried and offers to connect that host.
 * Otherwise it is a draft of each Agent Skill with every field marked by where its value came
 * from.
 */
export const AdminSourceNewPage = () => {
  const [check, { data: preview, isLoading, error }] = usePreviewSourceMutation();
  const { data: credentials } = useListAdminCredentialsQuery();
  // A connection made from here comes back with the address, so Check is one click away.
  const returned = useLocation().state as { url?: string } | null;
  const [url, setUrl] = useState(returned?.url ?? '');
  const [credentialId, setCredentialId] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void check({ url: url.trim(), ...(credentialId === '' ? {} : { credentialId }) });
  };

  return (
    <Box sx={pageGutterSx}>
      <Box sx={{ ...contentWidthSx, paddingBlock: 12 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ReturnLink to={ADMIN_REPOSITORIES_PATH} label="repositories" />
          <Typography variant="h1" component="h1">
            Add a repository
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 720 }}>
            Paste the address of a GitHub or GitLab repository holding an Agent Skill. Check reads it and shows what
            would be imported; nothing is saved until you pull it in.
          </Typography>
        </Box>

        <Box sx={{ ...sectionSpacingSx, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Box component="form" onSubmit={submit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 720 }}>
            <TextField
              label="Repository address"
              placeholder="https://github.com/and-digital/brand-diagram-skill"
              value={url}
              onChange={(event) => { setUrl(event.target.value); }}
              required
            />

            <TextField
              select
              label="Access key"
              value={credentialId}
              onChange={(event) => { setCredentialId(event.target.value); }}
              helperText="Leave on Find automatically and the toolkit tries a public read, then each GitHub App, then each token for that repository's own host."
            >
              <MenuItem value="">Find automatically</MenuItem>
              {(credentials ?? []).map((credential) => (
                <MenuItem key={credential.id} value={credential.id}>
                  {credential.label}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Button type="submit" variant="contained" disabled={url.trim() === '' || isLoading}>
                {isLoading ? 'Checking' : 'Check'}
              </Button>
              <Button component={RouterLink} to={ADMIN_NEW_CREDENTIAL_PATH} variant="text" size="small" state={{ url }}>
                Add an access key
              </Button>
            </Box>
          </Box>

          {error !== undefined && (
            <Typography role="alert" variant="body2" sx={{ color: 'error.main' }}>
              {describeQueryError(error)}
            </Typography>
          )}

          {preview !== undefined && preview.state === 'unsupported' && (
            <Typography role="alert" variant="body2" sx={{ color: 'error.main' }}>
              {preview.message}
            </Typography>
          )}

          {preview !== undefined && (preview.state === 'not-found' || preview.state === 'key-refused') && (
            <Typography role="alert" variant="body2" sx={{ color: 'error.main' }}>
              {preview.message}
            </Typography>
          )}

          {preview?.state === 'unavailable' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 720 }}>
              <Typography role="alert" variant="body2" sx={{ color: 'error.main' }}>
                {preview.message}
              </Typography>
            </Box>
          )}

          {preview?.state === 'needs-access' && <AccessOffer host={preview.host} message={preview.message} tried={preview.tried} url={url.trim()} />}

          {preview?.state === 'ready' && (
            <SourceDraft key={`${preview.source.id}:${preview.source.readAt}`} preview={preview} />
          )}
        </Box>
      </Box>
    </Box>
  );
};
